import { useCallback, useRef, useState } from 'react';

import {
  S3MultipartDropzoneAreaOnFileCreated,
  S3MultipartDropzoneAreaOnFileFailed,
  S3MultipartDropzoneAreaOnFilesAccepted,
  S3MultipartDropzoneAreaOnFileUploaded
} from '../../../../../../../../helpers/dropzone/S3MultipartDropzoneArea';

interface PendingFile {
  id: string;
  name: string;
  localUrl: string;
}

interface UseModelViewerImageBackgroundModalOptions {
  backgroundImageUrl: string | null;
  onSelectImage: (url: string) => void;
  onAddSessionUrl: (url: string) => void;
  onClose: () => void;
}

function useModelViewerImageBackgroundModal({
  backgroundImageUrl,
  onSelectImage,
  onAddSessionUrl,
  onClose
}: UseModelViewerImageBackgroundModalOptions) {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const cancelledIdsRef = useRef<Set<string>>(new Set());
  const addedFileIdsRef = useRef<Set<string>>(new Set());
  const previewedIdsRef = useRef<Set<string>>(new Set());
  const backgroundImageUrlRef = useRef<string | null>(backgroundImageUrl);
  backgroundImageUrlRef.current = backgroundImageUrl;

  const revokePending = useCallback((files: PendingFile[]) => {
    files.forEach((f) => URL.revokeObjectURL(f.localUrl));
  }, []);

  const handleFilesAccepted =
    useCallback<S3MultipartDropzoneAreaOnFilesAccepted>(
      (files) => {
        if (files.length === 0) return;

        const newItems = files.map((f) => ({
          id: f.id,
          name: f.name,
          localUrl: f.file
        }));

        newItems.forEach((item) => addedFileIdsRef.current.add(item.id));
        setPendingFiles((prev) => [...prev, ...newItems]);

        const last = newItems[newItems.length - 1];
        previewedIdsRef.current.add(last.id);
        onSelectImage(last.localUrl);
      },
      [onSelectImage]
    );

  const handleFileCreated = useCallback<S3MultipartDropzoneAreaOnFileCreated>(
    (file) => {
      if (addedFileIdsRef.current.has(file.id)) return;
      addedFileIdsRef.current.add(file.id);

      if (!previewedIdsRef.current.has(file.id)) {
        previewedIdsRef.current.add(file.id);
        onSelectImage(file.file);
      }

      setPendingFiles((prev) => [
        { id: file.id, name: file.name, localUrl: file.file },
        ...prev
      ]);
    },
    [onSelectImage]
  );

  const handleFileUploaded = useCallback<S3MultipartDropzoneAreaOnFileUploaded>(
    (id, _uploadedId, _updatedFile, file) => {
      let revokeUrl: string | undefined;

      setPendingFiles((prev) => {
        const removed = prev.find((f) => f.id === id);
        revokeUrl = removed?.localUrl;
        return prev.filter((f) => f.id !== id);
      });

      addedFileIdsRef.current.delete(id);
      previewedIdsRef.current.delete(id);

      if (cancelledIdsRef.current.has(id)) {
        cancelledIdsRef.current.delete(id);
        if (revokeUrl) URL.revokeObjectURL(revokeUrl);
        return;
      }

      const s3Url = file?.presignedUrl?.split('?')[0];
      if (!s3Url) {
        if (revokeUrl) URL.revokeObjectURL(revokeUrl);
        return;
      }

      onAddSessionUrl(s3Url);

      // Switch to S3 only if this blob is still the active background —
      // otherwise the user has already moved to a different background.
      if (revokeUrl && backgroundImageUrlRef.current === revokeUrl) {
        onSelectImage(s3Url);
      }

      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    },
    [onAddSessionUrl, onSelectImage]
  );

  const handleFileFailed = useCallback<S3MultipartDropzoneAreaOnFileFailed>(
    (id) => {
      setPendingFiles((prev) => {
        const removed = prev.find((f) => f.id === id);
        if (removed) URL.revokeObjectURL(removed.localUrl);
        return prev.filter((f) => f.id !== id);
      });
      addedFileIdsRef.current.delete(id);
      previewedIdsRef.current.delete(id);
      cancelledIdsRef.current.delete(id);
    },
    []
  );

  const handleCancelPending = useCallback((id: string) => {
    cancelledIdsRef.current.add(id);
    addedFileIdsRef.current.delete(id);
    previewedIdsRef.current.delete(id);
    setPendingFiles((prev) => {
      const removed = prev.find((f) => f.id === id);
      if (removed) URL.revokeObjectURL(removed.localUrl);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const handleSelectUploaded = useCallback(
    (url: string) => {
      onSelectImage(url);
      onClose();
    },
    [onSelectImage, onClose]
  );

  const handleClose = useCallback(() => {
    setPendingFiles((prev) => {
      revokePending(prev);
      return [];
    });
    cancelledIdsRef.current.clear();
    addedFileIdsRef.current.clear();
    previewedIdsRef.current.clear();
    onClose();
  }, [onClose, revokePending]);

  return {
    pendingFiles,
    handleFilesAccepted,
    handleFileCreated,
    handleFileUploaded,
    handleFileFailed,
    handleCancelPending,
    handleSelectUploaded,
    handleClose
  };
}

export default useModelViewerImageBackgroundModal;
